import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Search, Edit, Trash2, Eye, Settings, GitBranch, Users, CheckCircle, Clock, XCircle, Filter } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import ApprovalWorkflowsService from "@/api/approvalWorkflows.service";
import UsersService from "@/api/users.service";

interface ApprovalWorkflow {
  id: number;
  publicId: string;
  businessId: number;
  name: string;
  documentType: 'invoice' | 'expense' | 'bill' | 'purchase_order';
  triggerConditions: {
    amount_threshold?: number;
    departments?: string[];
    categories?: string[];
    vendors?: number[];
  };
  isActive: boolean;
  createdBy?: number;
  dateCreated: string;
  steps?: ApprovalStep[];
  requests?: ApprovalRequest[];
}

interface ApprovalStep {
  id: number;
  publicId: string;
  approvalWorkflowId: number;
  stepOrder: number;
  stepName: string;
  approverType: 'user' | 'role' | 'department_head' | 'amount_based';
  approverUserId?: number;
  approverRoleId?: number;
  requiredApprovals: number;
  isParallel: boolean;
  escalationTimeHours?: number;
  escalationUserId?: number;
  dateCreated: string;
  approverUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface ApprovalRequest {
  id: number;
  publicId: string;
  businessId: number;
  approvalWorkflowId: number;
  documentType: string;
  documentId: number;
  requestedBy: number;
  currentStepId?: number;
  status: 'pending' | 'approved' | 'rejected' | 'escalated';
  totalAmount?: number;
  currency?: string;
  requestData?: any;
  dateCreated: string;
  requesterUser?: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
}

export default function ApprovalWorkflows() {
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [approvalRequests, setApprovalRequests] = useState<ApprovalRequest[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [documentTypeFilter, setDocumentTypeFilter] = useState<string>("all");
  const [selectedWorkflow, setSelectedWorkflow] = useState<ApprovalWorkflow | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isStepsDialogOpen, setIsStepsDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState("workflows");

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    documentType: "",
    amountThreshold: "",
    departments: [] as string[],
    isActive: true
  });

  const [steps, setSteps] = useState<Partial<ApprovalStep>[]>([]);

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchData();
      fetchApprovalRequests();
      fetchUsers();
    }
  }, [selectedBusiness]);

  const fetchData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await ApprovalWorkflowsService.getApprovalWorkflows(selectedBusiness.id);
      if (response.data.result === "success") {
        setWorkflows(response.data.workflows || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch approval workflows",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalRequests = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await ApprovalWorkflowsService.getApprovalRequests(selectedBusiness.id);
      if (response.data.result === "success") {
        setApprovalRequests(response.data.requests || []);
      }
    } catch (error) {
      console.error("Failed to fetch approval requests:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await UsersService.getAllUsers();
      if (response.data.result === "success") {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  const handleCreate = async () => {
    if (!selectedBusiness || !formData.name || !formData.documentType) return;

    try {
      const workflowData = {
        businessId: selectedBusiness.id,
        name: formData.name,
        documentType: formData.documentType,
        triggerConditions: {
          amount_threshold: formData.amountThreshold ? parseFloat(formData.amountThreshold) : undefined,
          departments: formData.departments.length > 0 ? formData.departments : undefined
        },
        isActive: formData.isActive
      };

      await ApprovalWorkflowsService.createApprovalWorkflow(workflowData);
      toast({
        title: "Success",
        description: "Approval workflow created successfully",
      });
      setIsCreateDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create approval workflow",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async () => {
    if (!selectedWorkflow || !formData.name || !formData.documentType) return;

    try {
      const workflowData = {
        name: formData.name,
        documentType: formData.documentType,
        triggerConditions: {
          amount_threshold: formData.amountThreshold ? parseFloat(formData.amountThreshold) : undefined,
          departments: formData.departments.length > 0 ? formData.departments : undefined
        },
        isActive: formData.isActive
      };

      await ApprovalWorkflowsService.updateApprovalWorkflow(selectedWorkflow.id, workflowData);
      toast({
        title: "Success",
        description: "Approval workflow updated successfully",
      });
      setIsEditDialogOpen(false);
      setSelectedWorkflow(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update approval workflow",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedWorkflow) return;

    try {
      await ApprovalWorkflowsService.deleteApprovalWorkflow(selectedWorkflow.id);
      toast({
        title: "Success",
        description: "Approval workflow deleted successfully",
      });
      setIsDeleteDialogOpen(false);
      setSelectedWorkflow(null);
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete approval workflow",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      documentType: "",
      amountThreshold: "",
      departments: [],
      isActive: true
    });
    setSteps([]);
  };

  const openEditDialog = (workflow: ApprovalWorkflow) => {
    setSelectedWorkflow(workflow);
    setFormData({
      name: workflow.name,
      documentType: workflow.documentType,
      amountThreshold: workflow.triggerConditions.amount_threshold?.toString() || "",
      departments: workflow.triggerConditions.departments || [],
      isActive: workflow.isActive
    });
    setIsEditDialogOpen(true);
  };

  const openViewDialog = (workflow: ApprovalWorkflow) => {
    setSelectedWorkflow(workflow);
    setIsViewDialogOpen(true);
  };

  const openDeleteDialog = (workflow: ApprovalWorkflow) => {
    setSelectedWorkflow(workflow);
    setIsDeleteDialogOpen(true);
  };

  const openStepsDialog = (workflow: ApprovalWorkflow) => {
    setSelectedWorkflow(workflow);
    setIsStepsDialogOpen(true);
    // Fetch steps for this workflow
    fetchWorkflowSteps(workflow.id);
  };

  const fetchWorkflowSteps = async (workflowId: number) => {
    try {
      const response = await ApprovalWorkflowsService.getApprovalSteps(workflowId);
      if (response.data.result === "success") {
        setSteps(response.data.steps || []);
      }
    } catch (error) {
      console.error("Failed to fetch workflow steps:", error);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      approved: "bg-green-50 text-green-700 border-green-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      escalated: "bg-purple-50 text-purple-700 border-purple-200"
    };

    const icons = {
      approved: <CheckCircle className="h-4 w-4" />,
      pending: <Clock className="h-4 w-4" />,
      rejected: <XCircle className="h-4 w-4" />,
      escalated: <GitBranch className="h-4 w-4" />
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.pending}>
        {icons[status as keyof typeof icons] || icons.pending}
        <span className="ml-1 capitalize">{status}</span>
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const filteredWorkflows = workflows.filter(workflow => {
    const matchesSearch = workflow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         workflow.documentType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "active" && workflow.isActive) ||
                         (statusFilter === "inactive" && !workflow.isActive);
    const matchesDocumentType = documentTypeFilter === "all" || workflow.documentType === documentTypeFilter;
    
    return matchesSearch && matchesStatus && matchesDocumentType;
  });

  const filteredRequests = approvalRequests.filter(request => {
    const matchesSearch = request.documentType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || request.status === statusFilter;
    const matchesDocumentType = documentTypeFilter === "all" || request.documentType === documentTypeFilter;
    
    return matchesSearch && matchesStatus && matchesDocumentType;
  });

  const workflowColumns = [
    {
      accessorKey: "name",
      header: "Workflow Name",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium">{row.getValue("name")}</div>
            <div className="text-sm text-muted-foreground capitalize">
              {row.original.documentType.replace('_', ' ')}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "documentType",
      header: "Document Type",
      cell: ({ row }: any) => (
        <Badge variant="outline" className="capitalize">
          {row.getValue("documentType").replace('_', ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: "triggerConditions",
      header: "Trigger Conditions",
      cell: ({ row }: any) => {
        const conditions = row.getValue("triggerConditions");
        return (
          <div className="text-sm">
            {conditions.amount_threshold && (
              <div>Amount ≥ {formatCurrency(conditions.amount_threshold)}</div>
            )}
            {conditions.departments && conditions.departments.length > 0 && (
              <div>Departments: {conditions.departments.join(', ')}</div>
            )}
            {!conditions.amount_threshold && (!conditions.departments || conditions.departments.length === 0) && (
              <span className="text-muted-foreground">No conditions</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ row }: any) => (
        <Badge variant={row.getValue("isActive") ? "default" : "secondary"}>
          {row.getValue("isActive") ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: "dateCreated",
      header: "Created",
      cell: ({ row }: any) => formatDate(row.getValue("dateCreated")),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openViewDialog(row.original)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openStepsDialog(row.original)}
          >
            <Settings className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row.original)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDeleteDialog(row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const requestColumns = [
    {
      accessorKey: "documentType",
      header: "Document",
      cell: ({ row }: any) => (
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium capitalize">{row.getValue("documentType").replace('_', ' ')}</div>
            <div className="text-sm text-muted-foreground">
              ID: {row.original.documentId}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "totalAmount",
      header: "Amount",
      cell: ({ row }: any) => {
        const amount = row.getValue("totalAmount");
        const currency = row.original.currency || 'USD';
        return amount ? formatCurrency(amount, currency) : 'N/A';
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => getStatusBadge(row.getValue("status")),
    },
    {
      accessorKey: "requestedBy",
      header: "Requested By",
      cell: ({ row }: any) => {
        const requester = row.original.requesterUser;
        return requester ? (
          <div className="text-sm">
            {requester.firstName} {requester.lastName}
          </div>
        ) : (
          <span className="text-muted-foreground">Unknown</span>
        );
      },
    },
    {
      accessorKey: "dateCreated",
      header: "Created",
      cell: ({ row }: any) => formatDate(row.getValue("dateCreated")),
    },
  ];

  const statusCounts = {
    all: workflows.length,
    active: workflows.filter(w => w.isActive).length,
    inactive: workflows.filter(w => !w.isActive).length,
  };

  const requestStatusCounts = {
    all: approvalRequests.length,
    pending: approvalRequests.filter(r => r.status === 'pending').length,
    approved: approvalRequests.filter(r => r.status === 'approved').length,
    rejected: approvalRequests.filter(r => r.status === 'rejected').length,
    escalated: approvalRequests.filter(r => r.status === 'escalated').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Approval Workflows</h1>
          <p className="text-muted-foreground">
            Manage approval workflows and track approval requests
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Workflow
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="requests">Approval Requests</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows" className="space-y-6">
          {/* Status Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(statusCounts).map(([status, count]) => (
              <Card key={status} className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setStatusFilter(status)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground capitalize">
                        {status === 'all' ? 'Total Workflows' : `${status} Workflows`}
                      </p>
                      <p className="text-2xl font-bold">{count}</p>
                    </div>
                    <GitBranch className="h-8 w-8 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters & Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search workflows..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="document-type-filter">Document Type</Label>
                  <Select value={documentTypeFilter} onValueChange={setDocumentTypeFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="bill">Bill</SelectItem>
                      <SelectItem value="purchase_order">Purchase Order</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Workflows Table */}
          <Card>
            <CardHeader>
              <CardTitle>Workflows ({filteredWorkflows.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <DataTable
                  columns={workflowColumns}
                  data={filteredWorkflows}
                  section="approval-workflows"
                  isLoading={loading}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="requests" className="space-y-6">
          {/* Request Status Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(requestStatusCounts).map(([status, count]) => (
              <Card key={status} className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setStatusFilter(status)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground capitalize">
                        {status === 'all' ? 'Total' : status}
                      </p>
                      <p className="text-2xl font-bold">{count}</p>
                    </div>
                    {status !== 'all' && getStatusBadge(status).props.children[0]}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Approval Requests Table */}
          <Card>
            <CardHeader>
              <CardTitle>Approval Requests ({filteredRequests.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <DataTable
                  columns={requestColumns}
                  data={filteredRequests}
                  section="approval-requests"
                  isLoading={loading}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Workflow Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Approval Workflow</DialogTitle>
            <DialogDescription>
              Create a new approval workflow for document processing
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Workflow Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Invoice Approval Process"
                />
              </div>
              <div>
                <Label htmlFor="document-type">Document Type</Label>
                <Select value={formData.documentType} onValueChange={(value) => setFormData({ ...formData, documentType: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="bill">Bill</SelectItem>
                    <SelectItem value="purchase_order">Purchase Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="amount-threshold">Amount Threshold (Optional)</Label>
              <Input
                id="amount-threshold"
                type="number"
                value={formData.amountThreshold}
                onChange={(e) => setFormData({ ...formData, amountThreshold: e.target.value })}
                placeholder="e.g., 1000"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="is-active"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="is-active">Active</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!formData.name || !formData.documentType}>
                Create Workflow
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Workflow Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Approval Workflow</DialogTitle>
            <DialogDescription>
              Update the approval workflow information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-name">Workflow Name</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Invoice Approval Process"
                />
              </div>
              <div>
                <Label htmlFor="edit-document-type">Document Type</Label>
                <Select value={formData.documentType} onValueChange={(value) => setFormData({ ...formData, documentType: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select document type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="bill">Bill</SelectItem>
                    <SelectItem value="purchase_order">Purchase Order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="edit-amount-threshold">Amount Threshold (Optional)</Label>
              <Input
                id="edit-amount-threshold"
                type="number"
                value={formData.amountThreshold}
                onChange={(e) => setFormData({ ...formData, amountThreshold: e.target.value })}
                placeholder="e.g., 1000"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="edit-is-active"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
              />
              <Label htmlFor="edit-is-active">Active</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleEdit} disabled={!formData.name || !formData.documentType}>
                Update Workflow
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Workflow Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Workflow Details</DialogTitle>
            <DialogDescription>
              View approval workflow information
            </DialogDescription>
          </DialogHeader>
          {selectedWorkflow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Workflow Name</Label>
                  <p className="text-sm">{selectedWorkflow.name}</p>
                </div>
                <div>
                  <Label>Document Type</Label>
                  <p className="text-sm capitalize">{selectedWorkflow.documentType.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">
                    <Badge variant={selectedWorkflow.isActive ? "default" : "secondary"}>
                      {selectedWorkflow.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label>Created</Label>
                  <p className="text-sm">{formatDate(selectedWorkflow.dateCreated)}</p>
                </div>
              </div>
              <div>
                <Label>Trigger Conditions</Label>
                <div className="mt-1 p-3 bg-muted rounded-md text-sm">
                  {selectedWorkflow.triggerConditions.amount_threshold && (
                    <div>Amount threshold: {formatCurrency(selectedWorkflow.triggerConditions.amount_threshold)}</div>
                  )}
                  {selectedWorkflow.triggerConditions.departments && selectedWorkflow.triggerConditions.departments.length > 0 && (
                    <div>Departments: {selectedWorkflow.triggerConditions.departments.join(', ')}</div>
                  )}
                  {!selectedWorkflow.triggerConditions.amount_threshold && 
                   (!selectedWorkflow.triggerConditions.departments || selectedWorkflow.triggerConditions.departments.length === 0) && (
                    <span className="text-muted-foreground">No specific conditions set</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Workflow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedWorkflow?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} variant="destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Workflow Steps Dialog */}
      <Dialog open={isStepsDialogOpen} onOpenChange={setIsStepsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Workflow Steps</DialogTitle>
            <DialogDescription>
              Manage approval steps for "{selectedWorkflow?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {steps.length > 0 ? (
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <Card key={step.id || index}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                            {step.stepOrder}
                          </div>
                          <div>
                            <h4 className="font-medium">{step.stepName}</h4>
                            <p className="text-sm text-muted-foreground capitalize">
                              {step.approverType?.replace('_', ' ')} approval
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {step.isParallel ? 'Parallel' : 'Sequential'}
                          </Badge>
                          {step.approverUser && (
                            <span className="text-sm">
                              {step.approverUser.firstName} {step.approverUser.lastName}
                            </span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Settings className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No approval steps configured</p>
                <p className="text-sm">Add steps to define the approval process</p>
              </div>
            )}
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setIsStepsDialogOpen(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 